import React from 'react';

import {DashboardPage} from 'app/components/general/DashboardPage';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {useCurrentUserState} from 'app/current-user/api';
import {
  BreadcrumbItemStructure,
  type BreadcrumbProps,
} from 'app/general/structures/BreadcrumbItemStructure';
import {TEMPLATES_DASHBOARD_URL} from 'app/urls';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';

import {CompactTemplateListItem} from './CompactTemplateListItem';

import {useTemplates} from '../api';
import {useTemplateFilters} from '../hooks';
import {TemplateFetchParams, TemplateLocationParams} from '../types';

import type {WithRouterProps} from 'react-router';
import type {TemplateSerializedType} from 'sweet-types';

const templateCt = getContentType('template');

const templateSortOptions = [
  SORT_BY_TYPES.CREATION_DATE,
  SORT_BY_TYPES.ALPHABETHICALLY,
  SORT_BY_TYPES.RANK,
];

export function TemplatesDashboard(
  routerProps: WithRouterProps<unknown, TemplateLocationParams>
) {
  const currentUser = useCurrentUserState().data;

  return (
    <DashboardPage<
      TemplateSerializedType,
      TemplateLocationParams,
      TemplateFetchParams
    >
      {...routerProps}
      useDataHook={useTemplates}
      useFiltersHook={useTemplateFilters}
      contentType="template"
      pageTitle={gt('Templates')}
      enableNotifications={false}
      filterbar={({resetFilters, viewingAll}) => (
        <Filterbar
          ct="template"
          resetFilters={resetFilters}
          viewingAll={viewingAll}
          show={{
            search: true,
            team: false,
            tags: true,
            filters: false,
            sort: true,
          }}
          sortOptions={templateSortOptions}
          backendDefaultSort={SORT_BY_TYPES.CREATION_DATE}
        />
      )}
      renderItem={(template) => (
        <CompactTemplateListItem
          template={template}
          isSuperManager={currentUser?.is_super_manager ?? false}
          key={template.hashid}
        />
      )}
      messages={{
        counter: (count) => ngt('%s template', '%s templates', count),
        notFound: ({viewingAll, resetFilters}, NoResultsMessage) =>
          viewingAll ? (
            <NoResultsMessage
              icon={templateCt.icon}
              head={gt('No templates on your account')}
            ></NoResultsMessage>
          ) : (
            <NoResultsMessage
              icon={templateCt.icon}
              head={gt('No templates found with these filters')}
            >
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                <i className="fa fa-times" />{' '}
                {gt('Reset filters and view all templates')}
              </button>
            </NoResultsMessage>
          ),
      }}
    />
  );
}

export const TemplatesDashboardBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    icon={templateCt.icon}
    text={gt('Templates')}
    link={TEMPLATES_DASHBOARD_URL}
  />
);
